import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core'
import {HttpClient} from '@angular/common/http'
import {User} from '@auth/models/user.model'
import {SERVER_API_URL} from 'src/app/app.constants'

/**
 * Gecachte Adresse des Profilbilds.
 *
 * Ohne Cache flackert das Icon bei jedem Seitenaufbau, weil der Aufruf gegen
 * Slack läuft und einen Moment braucht. Der Schlüssel trägt den Werkzeugnamen,
 * damit er sich nicht mit anderen Anwendungen auf derselben Domäne beisst.
 */
const AVATAR_CACHE_KEY = 'abre_avatar_url'

@Component({
  selector: 'nav-header',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css']
})
export class NavigationComponent implements OnChanges {
  @Input() user: User | null
  @Output() public sidenavToggle = new EventEmitter()

  public avatarUrl: string | null = null

  /** Verhindert, dass derselbe Benutzer bei jedem Änderungslauf neu geladen wird. */
  private loadedForId: string | null = null

  constructor(private http: HttpClient) {
    try {
      this.avatarUrl = localStorage.getItem(AVATAR_CACHE_KEY) || null
    } catch {
      this.avatarUrl = null
    }
  }

  ngOnChanges(): void {
    this.loadSlackAvatar()
  }

  onToggleSidenav(): void {
    this.sidenavToggle.emit()
  }

  /**
   * Profilbild aus Slack holen - derselbe Endpunkt wie im Kalkulationstool und
   * im Einschreiben-Werkzeug.
   *
   * ⚠️ Anders als dort wird die Adresse hier VOLLSTÄNDIG gebaut: Der
   * Interceptor dieses Werkzeugs setzt bewusst kein Basis-Präfix, weil überall
   * absolute Adressen verwendet werden.
   *
   * Das Backend liefert die Adresse als JSON-Zeichenkette, also mit
   * umschliessenden Anführungszeichen - die müssen weg.
   */
  private loadSlackAvatar(): void {
    const id = this.user?.technicianId?.toString()
    if (!id || id === this.loadedForId) {
      return
    }
    this.loadedForId = id

    this.http
      .get(`${SERVER_API_URL}/getSlackProfilePictureByNumber`, {
        params: {userId: id},
        responseType: 'text'
      })
      .subscribe(
        (response) => {
          const cleaned = (response || '').toString().replace(/^"|"$/g, '').trim()
          if (cleaned) {
            this.avatarUrl = cleaned
            try {
              localStorage.setItem(AVATAR_CACHE_KEY, cleaned)
            } catch {
              /* Cache ist nur Bequemlichkeit */
            }
          }
        },
        () => {
          // Bei einem Fehler das gecachte Bild stehen lassen. Es zu löschen
          // liesse das Icon bei jeder kurzen Netzstörung verschwinden.
        }
      )
  }

  /** Bild nicht ladbar, etwa weil die Slack-Adresse abgelaufen ist. */
  onAvatarError(): void {
    this.avatarUrl = null
    try {
      localStorage.removeItem(AVATAR_CACHE_KEY)
    } catch {
      /* egal */
    }
  }

  /** Rückfall, solange kein Bild vorliegt: Initialen des angemeldeten Benutzers. */
  get initials(): string {
    const name = (this.user?.name || '').trim()
    if (!name) {
      return 'M'
    }
    const parts = name.split(/\s+/)
    const raw =
      parts.length > 1
        ? parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
        : name.slice(0, 2)
    return raw.toUpperCase()
  }
}
